import { Button, Form, Header, type Job, Segment, TraitementLong } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { RegieContext } from '@/contexts';
import { useListeSearchSort } from '@/hooks';
import {
  getFacturesImpayees,
  getFacturesResteAPayer,
  postRelance,
  printRelance,
  printRelanceAvantEcheance,
} from '@/routes';
import type { CycleType, FactureType, RelanceRecapDataType, RelanceType } from '@/types';
import FacturesImpayeesListe from './FacturesImpayeesListe';
import ModalConfirmRelances from './ModalConfirmRelances';
import ModalRecapRelances from './ModalRecapRelances';
import RelancesCycles from './RelancesCycles';
import Resume from './Resume';
import Seuil from './Seuil';
import Type from './Type';

type RelancesProps = {
  cycles: CycleType[];
  /** `avant` = relances avant la date limite de paiement ; `prolongee` = relances
   *  après la date limite (période de régie prolongée). Détermine le modèle/route d'édition. */
  relanceMode?: 'avant' | 'prolongee';
  onRedirectToSelection: () => void;
};

const Relances = (props: RelancesProps) => {
  const { cycles, relanceMode = 'prolongee', onRedirectToSelection } = props;

  const [seuilUtilisateur, setSeuilUtilisateur] = React.useState<number | null>();
  const [type, setType] = React.useState<'facture' | 'payeur'>('facture');
  const [totalReste, setTotalReste] = React.useState<number>(null);
  const [nombreRelancesPotentielles, setNombreRelancesPotentielles] = React.useState<number>(null);
  const [nombreMailsPotentiel, setNombreMailsPotentiel] = React.useState<number>(null);
  const [nombreFacturesMails, setNombreFacturesMails] = React.useState<number>(null);
  const [loading, setLoading] = React.useState<boolean | 'generation'>(false);
  const [generationModalOpen, setGenerationModalOpen] = React.useState<boolean>(false);
  const [job, setJob] = React.useState<number>(null);
  const [recapData, setRecapData] = React.useState<RelanceRecapDataType | null>(null);

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [regies, selectedRegie],
  );

  /** Seuil par défaut selon le type de relance : celui de la section
   *  « Relances avant la date limite de paiement » ou celui des relances classiques. */
  const seuilDefaut = React.useMemo(
    () => (relanceMode === 'avant' ? regie?.seuilRelanceAvantEcheance : regie?.seuilRelance) || 0,
    [relanceMode, regie?.seuilRelance, regie?.seuilRelanceAvantEcheance],
  );

  /** Seuil saisi, sinon le seuil par défaut. `??` et non `||` : un seuil saisi à 0
   doit l'emporter sur le seuil par défaut */
  const seuil = seuilUtilisateur ?? seuilDefaut;

  const getFactureImpayeeRoute = React.useMemo(
    () =>
      getFacturesImpayees({
        cycles: _.map(cycles, 'identifiant'),
        typeTransfert: type,
        seuil,
      }),
    [cycles, type, seuil],
  );

  const {
    items,
    loading: loadingFactures,
    total,
    setItems,
    setPage,
  } = useListeSearchSort({
    active: !_.isEmpty(cycles),
    getItems: getFactureImpayeeRoute,
  });

  const getResteAPayer = React.useCallback(async () => {
    setTotalReste(null);
    setNombreRelancesPotentielles(null);
    setNombreMailsPotentiel(null);
    setNombreFacturesMails(null);

    if (!_.isEmpty(cycles)) {
      const response = (await request.get(
        getFacturesResteAPayer({
          cycles: _.map(cycles, 'identifiant'),
          typeTransfert: type,
          seuil,
          avantEcheance: relanceMode === 'avant',
        }),
      )) as {
        totalResteAPayer: number;
        nombreRelancesPotentielles: number;
        nombreMailsPotentiel: number;
        nombreFacturesMails: number;
      };

      setTotalReste(response.totalResteAPayer);
      setNombreRelancesPotentielles(response.nombreRelancesPotentielles);
      setNombreMailsPotentiel(response.nombreMailsPotentiel);
      setNombreFacturesMails(response.nombreFacturesMails);
    }
  }, [cycles, type, seuil, relanceMode]);

  const handleGenerationModalToggle = React.useCallback(() => {
    setGenerationModalOpen((p) => !p);
  }, []);

  const handleGenerationConfirm = React.useCallback(() => {
    setLoading('generation');

    // Le modèle d'édition dépend du type de relance : « avant » ou « après » (prolongée)
    // la date limite de paiement.
    const printRoute = relanceMode === 'avant' ? printRelanceAvantEcheance : printRelance;

    request
      .post(postRelance(), {
        seuil,
        type,
        identifiantsCycles: _.map(cycles, 'identifiant'),
      })
      .then((relance: RelanceType) => {
        request
          .get(printRoute(relance.identifiant, true, nombreMailsPotentiel > 0), {
            headers: { Accept: 'application/json, application/pdf' },
          })
          .then((response: Job) => {
            const { identifiant: idJob } = response;

            setJob(idJob);
            setGenerationModalOpen(false);
          })
          .catch(() => {
            setLoading(false);
            setGenerationModalOpen(false);
            toast.error("Une erreur est survenue lors de l'édition des relances.");
          });
      })
      .catch(() => {
        setLoading(false);
        setGenerationModalOpen(false);
        toast.error('Une erreur est survenue lors de la génération des relances.');
      });
  }, [cycles, nombreMailsPotentiel, relanceMode, seuil, type]);

  const handleTypeChange = React.useCallback(
    (e, { value }) => {
      setItems([]);
      setPage(1);
      setType(value);
    },
    [setItems, setPage],
  );

  const handleSeuilChange = React.useCallback(
    (e, value) => {
      // si la valeur est la même que la précédente on fait rien
      // fix le bug si on rentre / sort sans rien faire
      if (seuilUtilisateur !== value) {
        setItems([]);
        setPage(1);
        setSeuilUtilisateur(value);
      }
    },
    [setItems, setPage, seuilUtilisateur],
  );

  const handleJobFinish = React.useCallback((finishedJob: Job) => {
    const { resultat, status } = finishedJob;

    setLoading(false);
    setJob(null);

    if (status === -1) {
      toast.error("Une erreur est survenue lors de l'édition des relances.");
      return;
    }

    const result = JSON.parse(resultat);

    if (result?.url) {
      window.open(result.url, '_blank');
    }

    setRecapData({
      url: result?.url || null,
      mailsSent: result?.mailsSent ?? 0,
    });
  }, []);

  const renderCustom = React.useCallback(
    (response: Job | null) => {
      if (!nombreMailsPotentiel) return null;

      let mailsSent = 0;

      if (response?.resultat) {
        try {
          const result = JSON.parse(response.resultat);

          if (result?.mailsSent != null) {
            mailsSent = result.mailsSent;
          }
        } catch {
          // resultat non parsable, on garde 0
        }
      }

      return (
        <div style={{ textAlign: 'center', marginTop: '1em' }}>
          {`${mailsSent} mail(s) envoyé(s) sur ${nombreMailsPotentiel}`}
        </div>
      );
    },
    [nombreMailsPotentiel],
  );

  const handleJobError = React.useCallback(() => {
    setLoading(false);
    setJob(null);

    toast.error("Une erreur est survenue lors de l'édition des relances.");
  }, []);

  const handleRecapClose = React.useCallback(() => {
    setRecapData(null);
    onRedirectToSelection();
  }, [onRedirectToSelection]);

  React.useEffect(() => {
    getResteAPayer();
  }, [getResteAPayer]);

  return (
    <Form>
      <Header
        as="h2"
        style={{
          margin: '0 0.5em 0 1em',
          display: 'flex',
        }}
      >
        <Header.Content style={{ flex: 1 }}>Relances</Header.Content>
        <Button
          content="Retourner à la liste"
          style={{ width: 250 }}
          disabled={loadingFactures}
          onClick={onRedirectToSelection}
        />
      </Header>
      <Segment style={{ margin: '1em', padding: '1.5em' }}>
        <RelancesCycles cycles={cycles} />
      </Segment>
      <div
        style={{
          margin: '1em',
          paddingRight: '2em',
          display: 'flex',
          flexDirection: 'row',
          columnGap: '1em',
        }}
      >
        <Segment
          style={{
            padding: '1.5em',
            margin: 0,
            flex: 2,
          }}
        >
          <Type
            header="Type de relance"
            subheader="Choisissez comment regrouper les relances."
            type={type}
            onTypeChange={handleTypeChange}
            disabled={_.isEmpty(cycles)}
          />
        </Segment>
        <Segment
          style={{
            padding: '1.5em',
            margin: 0,
            flex: 2,
          }}
        >
          <Seuil
            value={seuil}
            onSeuilChange={handleSeuilChange}
            disabled={_.isEmpty(cycles)}
            header="Seuil minimal de relance"
          />
        </Segment>
        {!_.isEmpty(cycles) && (
          <Segment
            style={{
              padding: '1.5em',
              margin: 0,
              backgroundColor: 'rgba(235, 242, 250, 0.5)',
              minWidth: 200,
            }}
            color="blue"
          >
            <Resume
              header="A relancer"
              loading={loadingFactures}
              total={total}
              totalReste={totalReste}
            />
          </Segment>
        )}
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            overflow: 'hidden',
            minWidth: 250,
          }}
        >
          <Button
            size="large"
            content="Générer les relances"
            positive
            style={{ width: 250, height: 70, margin: 0 }}
            disabled={
              loading !== false || loadingFactures || _.isNil(totalReste) || totalReste <= 0
            }
            loading={loading === 'generation'}
            onClick={handleGenerationModalToggle}
          />
          {generationModalOpen !== false && (
            <ModalConfirmRelances
              loading={loading === 'generation'}
              total={total}
              totalReste={totalReste}
              nombreRelances={nombreRelancesPotentielles}
              nombreMailsPotentiel={nombreMailsPotentiel}
              nombreFacturesMails={nombreFacturesMails}
              onCancel={handleGenerationModalToggle}
              onConfirm={handleGenerationConfirm}
            />
          )}
          {!_.isNil(job) && (
            <TraitementLong
              title="Édition des relances en cours"
              identifiantJob={job}
              onFinish={handleJobFinish}
              onError={handleJobError}
              refreshInterval={3000}
            >
              {renderCustom}
            </TraitementLong>
          )}
          {recapData !== null && (
            <ModalRecapRelances
              total={total}
              totalReste={totalReste}
              nombreRelances={nombreRelancesPotentielles}
              nombreMailsPotentiel={nombreMailsPotentiel}
              nombreFacturesMails={nombreFacturesMails}
              mailsSent={recapData.mailsSent}
              pdfUrl={recapData.url}
              onClose={handleRecapClose}
            />
          )}
        </div>
      </div>
      <Segment basic>
        <Segment style={{ padding: 0, margin: 0 }}>
          <FacturesImpayeesListe
            active={!_.isEmpty(cycles)}
            items={items as FactureType[]}
            loading={loadingFactures}
            total={total}
            setPage={setPage}
          />
        </Segment>
      </Segment>
    </Form>
  );
};

export default Relances;
